import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { portalPathForRole } from "@/lib/authHelpers";
import { captureNoAccessRedirect } from "@/lib/sentry";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Loader2 } from "lucide-react";

export default function NoAccess() {
  const { t, language } = useLanguage();
  const { signOut, user, activeRole, isLoading, isRefreshing } = useAuth();
  const navigate = useNavigate();

  // Self-heal: a valid user can be transiently routed here during a tab-return
  // token refresh (auth resolves with user set but activeRole momentarily null,
  // so RoleGate redirects to /no-access). Once auth settles on a real role,
  // bounce them to their portal instead of stranding them on this dead-end.
  // Gated on !isRefreshing so we don't compute a path mid-refresh.
  const recoveredPath =
    !isLoading && !isRefreshing && activeRole ? portalPathForRole(activeRole) : null;
  const willSelfHeal = !!recoveredPath && recoveredPath !== "/no-access";

  // Genuinely stranded: auth has settled (not loading, no refresh in flight) and
  // there's no role to bounce to. THIS is the state worth reporting — after the
  // grace window + self-heal have had their chance.
  const stranded = !isLoading && !isRefreshing && !willSelfHeal;

  useEffect(() => {
    // Only report a GENUINE authenticated-but-roleless stranding. An
    // unauthenticated user that lands here is immediately redirected to /auth
    // (see the `if (!user)` guard below) — that self-healing transient was
    // firing ~all of the "[auth] landed on /no-access" warnings (every event
    // was authenticated=false). Gating on `user` removes that noise while still
    // catching the case that actually needs eyes: a logged-in account with no
    // resolvable role after the grace window + self-heal had their chance.
    if (stranded && user) {
      captureNoAccessRedirect({ authenticated: true, activeRole, route: "/no-access" });
    }
  }, [stranded, user, activeRole]);

  const handleSignOut = async () => {
    // /no-access is a PUBLIC route, so clearing auth state alone leaves the user
    // stranded here (nothing redirects a logged-out user off a public page).
    // Navigate explicitly so Sign Out actually leaves the page. `finally` ensures
    // we navigate even if signOut throws — never trap the user on this dead-end.
    try {
      await signOut();
    } catch {
      // Best-effort: even if sign-out fails, still leave the page below so the
      // user is never trapped on this dead-end.
    } finally {
      navigate("/auth", { replace: true });
    }
  };

  if (willSelfHeal) {
    return <Navigate to={recoveredPath!} replace />;
  }

  // During initial load or an in-flight refresh, WAIT — don't flash the
  // dead-end card before the rotated token resolves the role.
  if (isLoading || isRefreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Unauthenticated and settled → this "account not set up" dead-end isn't for
  // them; send them to login. /no-access is a PUBLIC route, so without this an
  // un-logged-in user who lands here (a guard/redirect artifact, or a session
  // lost after a guard already computed /no-access) is stranded with no way out
  // but the Sign-Out button. The card below is reserved for a genuinely
  // AUTHENTICATED-but-roleless account. (Confirmed via Sentry no_access_redirect
  // events tagged authenticated=false.)
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <Card className="w-full max-w-md mx-auto text-center">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-amber-100 p-3">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">
            {t("الحساب غير مُهيأ", "Account Not Set Up")}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {t(
              "حسابك غير مُهيأ بعد. يرجى التواصل مع الدعم للحصول على المساعدة.",

              "Your account has not been set up yet. Please contact support for assistance.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "بعد أن يقوم فريق الدعم بإعداد حسابك، يمكنك تسجيل الدخول مرة أخرى.",

              "Once support sets up your account, you can sign in again.",
            )}
          </p>
          <Button
            onClick={handleSignOut}
            variant="default"
            className="w-full"
          >
            {t("تسجيل الخروج", "Sign Out")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
